/*
 *  ************************************************************************
 *  *  © [2015 - 2020] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import get from "lodash/get";
import { AccessType } from "@quintype/components";
import { connect } from "react-redux";
import { CaptureUserData } from "../../molecules/capture-user-data/index";
import { MEMBER_METADATA_UPDATED, SHOW_MEMBER_METADATA_POPUP } from "../../../constants";
import { openPaymentScreen, getSubscriptionLayout, navigateToSubscriptionSuccessPage } from "./payment-utils";
import { getLocaleDate } from "../../../../isomorphic/utils";
import { Popup } from "./molecules/pop-up";
import LoaderOverlay from "../../molecules/loader-overlay";
import { getLoginLink } from "../login/sso-utils";

export const SubscriptionTemplate = (props) => {
  const [isSubscribeButtonClicked, setSubscribeButtonClicked] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState({});
  const [isWaitingForUserToLogin, setWaitingForUserToLogin] = useState(false);
  const [isAccessTypeUserSet, setAccessTypeUserSet] = useState(false);
  const [isWaitingForPayment, setWaitingForPayment] = useState(false);
  const [isAccessTypeUserSetLoading, setAccessTypeUserSetLoading] = useState(false);
  const [memberId, setMemberId] = useState(props?.member?.id);
  const [popupMsg, setPopupMsg] = useState("");
  const [showPopup, setPopup] = useState(false);
  const [loader, setLoader] = useState(false);
  const sso = get(props, ["config", "pagebuilder-config", "general", "sso"], {});
  const sketchesHost = get(props, ["config", "sketches-host"], '');
  const whiteListedUrlParams = get(props, ["config", "pagebuilder-config", "general", "others", "whiteListedUrlParams"], []);
  
  const defaultSubscriptionHandler = async (e, plan = "") => {
    e.preventDefault();
    e.stopPropagation();
    setSubscribeButtonClicked(true);
    props?.showMemberMetadataPopup(true);
    if (plan) setSelectedPlan(plan);

    if (!props?.member) {
      setWaitingForUserToLogin(true);
      getLoginLink(props?.member, sso, sketchesHost, whiteListedUrlParams);
    }

    if (!props?.captureShippingAddress) props?.subscriberMetadata(true);
  };

  useEffect(() => {
    const initiatePaymentProcess = async () => {
      if (props?.member?.id !== memberId) {
        setWaitingForUserToLogin(false);
        setMemberId(props?.member?.id);
      }

      if (
        props?.member &&
        isSubscribeButtonClicked &&
        selectedPlan &&
        isAccessTypeUserSet &&
        !isWaitingForPayment &&
        !isWaitingForUserToLogin &&
        props?.isMemberMetadataUpdated
      ) {
        setWaitingForPayment(true);
        try {
          setLoader(true);
          const subscription = await openPaymentScreen(selectedPlan, props);
          subscription && navigateToSubscriptionSuccessPage(props);
        } catch ({ error }) {
          if (error?.code === "subscription_preview_active_subscription") {
            const { end_timestamp: endTimestamp, plan_occurrence: planOccurrence } = get(
              error,
              ["payload", "subscription"],
              {}
            );
            if (planOccurrence === "One Time") {
              const updatedTimestamp = endTimestamp ? getLocaleDate(endTimestamp, props.localizedMonths) : "";
              const popupMessage = `Dear user, you already have an <span class="highlight">active subscription</span> which expires on <span class="nowrap highlight">${updatedTimestamp}</span>. You are not permitted to repurchase the same plan until then.`;
              setPopupMsg(popupMessage);
              setPopup(true);
            }
          } else {
            console.error("Subscription", error);
          }
        } finally {
          setLoader(false);
          setWaitingForPayment(false);
          setSubscribeButtonClicked(false);
          setSelectedPlan({});
        }
      }
      const pageSetting = get(props, ["config", "pagebuilder-config", "subscription", "pageSetting"], {});
      const enableLoginlessSubscription = get(pageSetting, ["enableLoginlessSubscription"], false);

      if ((props?.member || enableLoginlessSubscription) && !isAccessTypeUserSet && !isAccessTypeUserSetLoading && props?.isATGlobalAvailable) {
        setAccessTypeUserSetLoading(true);


        // Bad implementation. Currently AccessType component not exposing a helper
        // fn to call setUser with the jwtToken.
        // Added issue to fix these issues: https://github.com/quintype/quintype-node-components/issues/145
        props?.initAccessType(() => {
          setAccessTypeUserSet(true);
          setAccessTypeUserSetLoading(false);
        });
      }
    };
    initiatePaymentProcess();
  }, [
    props.member,
    isSubscribeButtonClicked,
    selectedPlan,
    isAccessTypeUserSet,
    isWaitingForUserToLogin,
    props.isMemberMetadataUpdated,
    isAccessTypeUserSetLoading,
    props.isATGlobalAvailable,
    props.config,
  ]);

  return (
    <>
      {loader && <LoaderOverlay />}
      {getSubscriptionLayout({ ...props, defaultSubscriptionHandler, isAccessTypeUserSetLoading })}
      {props?.member && isSubscribeButtonClicked && props?.captureShippingAddress && (
        <CaptureUserData
          member={props?.member}
          getSubscriberMetadata={props?.getSubscriberMetadata}
          setSubscriberMetadata={props?.setSubscriberMetadata}
        />
      )}
      {showPopup && (
        <Popup
          message={popupMsg}
          title="Hold On!"
          buttonsData={[
            {
              name: "Okay",
              onClick: () => {
                setPopup(false);
                setPopupMsg("");
              },
              styleName: "primary",
            },
          ]}
          onClose={() => {
            setPopup(false);
            setPopupMsg("");
          }}
        />
      )}
    </>
  );
};

SubscriptionTemplate.propTypes = {
  initAccessType: PropTypes.func,
  initRazorPayPayment: PropTypes.func,
  initOmisePayment: PropTypes.func,
  initPaypalPayment: PropTypes.func,
  initAdyenPayment: PropTypes.func,
  initStripePayment: PropTypes.func,
  initLoginlessSubscription: PropTypes.func,
  subscriberMetadata: PropTypes.func,
  getSubscriptionForUser: PropTypes.func,
  navigateTo: PropTypes.func,
  member: PropTypes.object,
  getSubscription: PropTypes.func,
  currentPath: PropTypes.string,
  localizedSubscribe: PropTypes.string,
  captureShippingAddress: PropTypes.bool,
  isMemberMetadataUpdated: PropTypes.bool,
  getSubscriberMetadata: PropTypes.func,
  setSubscriberMetadata: PropTypes.func,
  localizedTime: PropTypes.shape({
    weeks: PropTypes.string,
    months: PropTypes.string,
    years: PropTypes.string,
    days: PropTypes.string,
  }),
};

function SubscriptionBase(props) {
  const [isATGlobalAvailable, setATGlobal] = useState(false);
  const pbAccessTypeConfig = get(props, ["config", "pagebuilder-config", "general", "accesstypeIntegration"], {});
  const isStaging = get(pbAccessTypeConfig, ["accesstypeMode"]) === "staging";
  const {
    accesstypeKey = "",
    bridgeKeeperIntegrationId = "",
    enableAccesstype = false,
    captureShippingAddress = false,
    accessTypeProdHost = "",
    accessTypeStgHost = "",
  } = pbAccessTypeConfig;

  if (!enableAccesstype) return null;
  const sketchesHost = get(props, ["config", "sketches-host"], '');
  const email = get(props, ["member", "email"]);
  const phone = get(props, ["member", "metadata", "phone-number"], "1234");
  const storyId = get(props, ["story", "id"]);

  return (
    <AccessType
      enableAccesstype={enableAccesstype}
      isStaging={isStaging}
      accessTypeKey={accesstypeKey}
      accessTypeBkIntegrationId={bridgeKeeperIntegrationId}
      email={email}
      phone={phone}
      storyId={storyId}
      prodHost={accessTypeProdHost}
      stagingHost={accessTypeStgHost}
      sketchesHost={sketchesHost}
      onATGlobalSet={() => {
        setATGlobal(true);
      }}
    >
      {({
        initAccessType,
        initRazorPayPayment,
        initPaypalPayment,
        initOmisePayment,
        initAdyenPayment,
        initPaytrailPayment,
        initStripePayment,
        initLoginlessSubscription,
        getSubscriberMetadata,
        setSubscriberMetadata,
      }) => (
        <SubscriptionTemplate
          initPaypalPayment={initPaypalPayment}
          initOmisePayment={initOmisePayment}
          initRazorPayPayment={initRazorPayPayment}
          initAccessType={initAccessType}
          initAdyenPayment={initAdyenPayment}
          initPaytrailPayment={initPaytrailPayment}
          initStripePayment={initStripePayment}
          initLoginlessSubscription={initLoginlessSubscription}
          setSubscriberMetadata={setSubscriberMetadata}
          getSubscriberMetadata={getSubscriberMetadata}
          captureShippingAddress={captureShippingAddress}
          isATGlobalAvailable={isATGlobalAvailable}
          pbAccessTypeConfig={pbAccessTypeConfig}
          {...props}
        />
      )}
    </AccessType>
  );
}

const mapStateToProps = (state) => {
  const { enableLocalization = false, localizedElements = {} } = get(
    state,
    ["qt", "config", "pagebuilder-config", "general", "localization"],
    {}
  );
  return {
    config: get(state, ["qt", "config"], {}),
    subscriptions: get(state, ["subscriptions"], null),
    member: get(state, ["member"], null),
    localizedSubscribe: enableLocalization && get(localizedElements, ["buttonLabels", "subscribe"]),
    isMemberMetadataUpdated: get(state, ["memberMetadata", "isMemberMetadataUpdated"], false),
    userMetaData: get(state, ["memberMetadata", "data"], {}),
    localizedTime: enableLocalization ? get(localizedElements, ["publishTime"], {}) : {},
    enableDarkMode: get(state, ["header", "isDarkModeEnabled"], false),
  };
};

const mapDispatchToProps = (dispatch) => ({
  navigateTo: (url) => global.app.navigateToPage(dispatch, url),
  subscriberMetadata: (isMemberMetadataUpdated) => dispatch({ type: MEMBER_METADATA_UPDATED, isMemberMetadataUpdated }),
  showMemberMetadataPopup: (showMemberMetadataPopup) =>
    dispatch({ type: SHOW_MEMBER_METADATA_POPUP, showMemberMetadataPopup }),
});

export const Subscription = connect(mapStateToProps, mapDispatchToProps)(SubscriptionBase);
